/**
 * The brand session, as React state.
 *
 * Replaces the live page's `let S` - a module-level mutable object that every
 * one of 196 functions could reach and reassign, and which the browser smoke
 * test can only touch through the string form of `page.evaluate` because it is
 * a classic-script global rather than a property of `window`.
 *
 * Deliberately small. It holds what the old `S` held and nothing more:
 *
 *   brand · subscriptionStatus · onboardingStep · route · obStep
 *
 * `route` is stored but not yet wired to `hashchange`; navigation is stage 4,
 * and putting a listener here now would spread routing across two stages.
 *
 * @module web/state/BrandContext
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { MembershipRequiredError, SessionExpiredError, get, hasPendingFinancialRequest, post, raw, setActiveBrandId, setAuthToken } from "../api/client.ts";
import type { BrandResponse, SubscriptionStatus } from "../api/types.ts";
import { bootstrapSession, type AuthStatus, type Session } from "./session.ts";
import { clearVaultParam, isVaultReturn, resolveVaultReturn } from "./vault.ts";
import { FIRST_STEP } from "../screens/onboarding/Progress.tsx";
import type { Notice, ToastTone } from "../components/Toast.tsx";
import { selectionKey, selectInitialBrand } from "./selection.ts";

export interface BrandChoice {
  id: string;
  name: string;
  role: "owner" | "member" | "strategist";
  isActive: boolean;
}

export interface BrandContextValue {
  status: AuthStatus;
  brand: BrandResponse | null;
  memberships: BrandChoice[];
  switchBrand: (brandId: string) => Promise<boolean>;
  createBrand: (name: string, website?: string) => Promise<boolean>;
  draftDirty: boolean;
  setDraftDirty: (dirty: boolean) => void;
  error: string | null;

  /** Mirrors of the fields the old `S` cached beside the brand. */
  subscriptionStatus: SubscriptionStatus;
  onboardingStep: number;

  /** Which screen is showing. Stage 4 gives this a router. */
  route: string;
  setRoute: (route: string) => void;

  /** Which onboarding step is showing. Stage 3 gives this the flow. */
  obStep: number;
  setObStep: (step: number) => void;

  /** Re-read the brand, e.g. after a webhook has changed it. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;

  /** The toast currently showing, if any. */
  notice: Notice | null;
  notify: (message: string, tone?: ToastTone) => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

/**
 * Re-read the brand into the given setter.
 *
 * Shared by the provider's `refresh` and by the vault-return path, which runs
 * inside the mount effect and must not take `refresh` as a dependency - that
 * would re-run the whole bootstrap every time its identity changed.
 */
async function refreshInto(set: (s: Session) => void): Promise<void> {
  try {
    const brand = await get<BrandResponse>("/api/brand");
    set({ status: "authenticated", brand, error: null });
  } catch {
    // Leave the session as it is: a failed re-read is not a reason to sign
    // someone out of a session the server still honours.
  }
}

/** The state a page load starts from, before the server has answered. */
const INITIAL: Session = { status: "loading", brand: null, error: null };

export function BrandProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(INITIAL);
  const [memberships, setMemberships] = useState<BrandChoice[]>([]);
  const [draftDirty, setDraftDirty] = useState(false);
  const generation = useRef(0);
  const [route, setRoute] = useState<string>(() => window.location.hash.replace(/^#/, "") || "overview");
  const [obStep, setObStep] = useState(FIRST_STEP);
  const [notice, setNotice] = useState<Notice | null>(null);

  const notify = useCallback((message: string, tone: ToastTone = "") => {
    // The id makes two identical messages in a row two distinct notices, so the
    // second one animates instead of being silently deduplicated by React.
    setNotice(n => ({ message, tone, id: (n?.id ?? 0) + 1 }));
  }, []);

  useEffect(() => {
    let live = true;
    const vaultReturn = isVaultReturn(window.location.search);

    void (async () => {
      setActiveBrandId(null);
      const s = await bootstrapSession(window.location.search);
      // The provider can unmount mid-flight in development's StrictMode double
      // render; setting state after that is a warning and a leak.
      if (!live) return;
      if (s.status === "authenticated" && s.brand) {
        let chosen = s.brand;
        if (s.brand.self?.id) {
          try {
            const list = await get<{ brands: BrandChoice[] }>("/api/me/brands");
            if (!live) return;
            const accepted = new URLSearchParams(window.location.search).get("invite") === "accepted";
            const stored = window.sessionStorage.getItem(selectionKey(s.brand.self.id));
            const id = selectInitialBrand(s.brand.id, stored, list.brands, accepted);
            setActiveBrandId(id);
            if (id !== s.brand.id) chosen = await get<BrandResponse>("/api/brand");
            if (!live) return;
            setMemberships(list.brands.map(b => ({ ...b, isActive: b.id === chosen.id })));
            window.sessionStorage.setItem(selectionKey(s.brand.self.id), chosen.id);
          } catch {
            // The server brand remains authoritative when the list cannot be
            // read. Never preserve an unverified saved selection as a header.
            setActiveBrandId(s.brand.id);
            setMemberships([{ id: s.brand.id, name: s.brand.name, role: s.brand.role ?? "owner", isActive: true }]);
          }
        } else {
          setActiveBrandId(s.brand.id);
          setMemberships([{ id: s.brand.id, name: s.brand.name, role: "owner", isActive: true }]);
        }
        setSession({ ...s, brand: chosen });
        if (new URLSearchParams(window.location.search).get("invite") === "accepted") {
          const url = new URL(window.location.href);
          url.searchParams.delete("invite");
          window.history.replaceState(null, "", url.toString());
        }
      } else {
        setSession(s);
      }

      // Resume where they left off. Set ONCE, from the bootstrap: deriving it
      // on every refresh would undo someone's progress the moment anything
      // re-read the brand.
      //
      // `onboarding_step` records the step they are ON - `advance()` writes the
      // step it has just moved TO, not the one it finished. The live page read
      // it back as `+ 1` anyway, so closing the tab on step 3 and returning
      // reopened on step 4 and that step was never seen. Read back as-is, and
      // floored at FIRST_STEP so the retired step 1 (and a fresh brand's 0)
      // both land on the first screen that still exists rather than on nothing.
      if (s.status === "authenticated" && s.brand) {
        setObStep(Math.max(FIRST_STEP, s.brand.onboardingStep ?? 0));
      }

      if (!vaultReturn || s.status !== "authenticated") return;
      // Resolve the card on whichever tab came back from Whop, drop the
      // parameter, then re-read the brand so hasCardOnFile (and any cleared
      // block) takes effect without a reload. See web/state/vault.ts.
      const outcome = await resolveVaultReturn();
      if (!live) return;
      clearVaultParam();
      notify(outcome.message, outcome.ok ? "success" : "error");
      if (outcome.ok) await refreshInto(setSession);
    })();

    return () => { live = false; };
  }, [notify]);

  const refresh = useCallback(async () => {
    const requestGeneration = generation.current;
    try {
      const brand = await get<BrandResponse>("/api/brand");
      if (requestGeneration !== generation.current) return;
      setSession({ status: "authenticated", brand, error: null });
    } catch (e) {
      if (requestGeneration !== generation.current) return;
      if (e instanceof MembershipRequiredError) {
        setActiveBrandId(null);
        try {
          // The selected membership may have been removed while this person
          // still belongs to another brand. Let the server choose a remaining
          // membership before showing the no-access state.
          const remaining = await get<BrandResponse>("/api/brand");
          const list = await get<{ brands: BrandChoice[] }>("/api/me/brands");
          if (requestGeneration !== generation.current) return;
          setActiveBrandId(remaining.id);
          if (remaining.self?.id) window.sessionStorage.setItem(selectionKey(remaining.self.id), remaining.id);
          setMemberships(list.brands.map(b => ({ ...b, isActive: b.id === remaining.id })));
          setDraftDirty(false);
          setSession({ status: "authenticated", brand: remaining, error: null });
        } catch {
          if (requestGeneration !== generation.current) return;
          setMemberships([]);
          setSession({ status: "membership-required", brand: null, error: null });
        }
        return;
      }
      if (e instanceof SessionExpiredError) {
        setAuthToken(null);
        setActiveBrandId(null);
        setSession({ status: "signed-out", brand: null, error: null });
        return;
      }
      // A refresh that fails for another reason must NOT throw the client out
      // of a session the server still honours. Keep the brand we have.
      setSession(prev => ({ ...prev, error: e instanceof Error ? e.message : "Could not refresh." }));
    }
  }, []);

  const switchBrand = useCallback(async (brandId: string): Promise<boolean> => {
    const oldBrand = session.brand;
    const oldPersonId = oldBrand?.self?.id;
    if (!memberships.some(b => b.id === brandId) || !oldBrand || !oldPersonId) return false;
    if (brandId === oldBrand.id) return true;
    if (hasPendingFinancialRequest()) {
      notify("Finish the payment confirmation before switching brands.", "error");
      return false;
    }
    if (draftDirty && !window.confirm(`Keep this draft with ${oldBrand.name} and switch brands?`)) return false;
    const previous = session;
    const currentGeneration = ++generation.current;
    setSession({ status: "loading", brand: null, error: null });
    setActiveBrandId(brandId);
    try {
      const brand = await get<BrandResponse>("/api/brand");
      if (currentGeneration !== generation.current) return false;
      if (brand.id !== brandId || brand.self?.id !== oldPersonId) throw new Error("Brand selection could not be verified.");
      window.sessionStorage.setItem(selectionKey(brand.self.id), brandId);
      setMemberships(rows => rows.map(b => ({ ...b, isActive: b.id === brandId })));
      setDraftDirty(false);
      setSession({ status: "authenticated", brand, error: null });
      return true;
    } catch (e) {
      if (currentGeneration !== generation.current) return false;
      setActiveBrandId(oldBrand.id);
      setSession(previous);
      notify(e instanceof Error ? e.message : "Could not switch brands.", "error");
      return false;
    }
  }, [memberships, session, draftDirty, notify]);

  const createBrand = useCallback(async (name: string, website = ""): Promise<boolean> => {
    const current = session.brand;
    if (!current?.self?.id || !current.billingOwner) return false;
    if (hasPendingFinancialRequest()) {
      notify("Finish the payment confirmation before creating another brand.", "error"); return false;
    }
    if (draftDirty && !window.confirm(`Keep this draft with ${current.name} and create a new brand?`)) return false;
    const priorIds = new Set(memberships.map(m => m.id));
    const currentGeneration = ++generation.current;
    try {
      let chosenId: string;
      let choices: BrandChoice[];
      try {
        const result = await post<{ brand: { id: string }; brands: BrandChoice[] }>("/api/brands", { name: name.trim(), website: website.trim() || undefined });
        chosenId = result.brand.id;
        choices = result.brands;
      } catch (error) {
        // The write may have succeeded while its response was lost. Read the
        // durable membership list before offering the user another attempt.
        const list = await get<{ brands: BrandChoice[] }>("/api/me/brands");
        const found = list.brands.find(b => !priorIds.has(b.id) && b.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase());
        if (!found) throw error;
        chosenId = found.id;
        choices = list.brands;
      }
      if (currentGeneration !== generation.current) return false;
      setSession({ status: "loading", brand: null, error: null });
      setActiveBrandId(chosenId);
      const next = await get<BrandResponse>("/api/brand");
      if (currentGeneration !== generation.current) return false;
      if (next.id !== chosenId || next.self?.id !== current.self.id) throw new Error("New brand could not be verified.");
      window.sessionStorage.setItem(selectionKey(current.self.id), chosenId);
      setMemberships(choices.map(b => ({ ...b, isActive: b.id === chosenId })));
      setDraftDirty(false);
      setSession({ status: "authenticated", brand: next, error: null });
      notify(`${next.name} is ready for briefs. It shares this account's card and wallet.`, "success");
      return true;
    } catch (error) {
      if (currentGeneration !== generation.current) return false;
      setActiveBrandId(current.id);
      setSession(session);
      notify(error instanceof Error ? error.message : "Could not create the brand. Check the list before retrying.", "error");
      return false;
    }
  }, [session, memberships, draftDirty, notify]);

  const signOut = useCallback(async () => {
    ++generation.current;
    // Server-side revocation, not just a forgotten cookie: clearing it locally
    // left a copied cookie usable for its full 30 days.
    try {
      await post("/api/auth/logout");
    } catch {
      // Already gone, or unreachable. Either way this browser is signing out.
    }
    setAuthToken(null);
    setActiveBrandId(null);
    setMemberships([]);
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith("aditor-brief-draft:") || key.startsWith("aditor-active-brand:")) window.sessionStorage.removeItem(key);
    }
    setSession({ status: "signed-out", brand: null, error: null });
  }, []);

  // A deliberate, narrow test hook.
  //
  // The live page exposes its whole state object as a classic-script global, so
  // the browser suite drives onboarding with `S.obStep = 4; showOnboarding()`.
  // React has no such global, and gotcha 4 of the migration plan is explicit
  // that leaving that probe pointing at nothing is the trap. So the app states
  // what it allows instead: move the step pointer, which is exactly what
  // clicking Continue does and nothing more. No data, no credentials, no writes.
  useEffect(() => {
    (window as unknown as { __aditor?: unknown }).__aditor = {
      setObStep,
      // The money path, through the app's OWN request helper. The assertion
      // that uses this is about which credential the helper sends - the bug it
      // exists for shipped as `Authorization: Bearer null` on a cookie-only
      // session, which the server read as the token and refused.
      postMoney: (path: string) => raw(path, { method: "POST" }).then(r => r.status),
    };
    return () => { delete (window as unknown as { __aditor?: unknown }).__aditor; };
  }, []);

  const value = useMemo<BrandContextValue>(() => ({
    status: session.status,
    brand: session.brand,
    memberships,
    switchBrand,
    createBrand,
    draftDirty,
    setDraftDirty,
    error: session.error,
    subscriptionStatus: session.brand?.subscriptionStatus ?? "active",
    onboardingStep: session.brand?.onboardingStep ?? 0,
    route,
    setRoute,
    obStep,
    setObStep,
    refresh,
    signOut,
    notice,
    notify,
  }), [session, memberships, switchBrand, createBrand, draftDirty, route, obStep, refresh, signOut, notice, notify]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

/** The brand session. Throws outside a BrandProvider, rather than handing back a null. */
export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error("useBrand must be used inside a <BrandProvider>");
  return ctx;
}
